import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import { useAlertContext } from 'Data/context/Alerts';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import DeleteButton from '../Buttons/DeleteButton';
import SaveButton from '../Buttons/SaveButton';
import DetailTable from '../DetailPanel/DetailTable';

let DM = new DataManager();

const AddressDetailTable = ({
  deleteAddressLinks,
  addressId,
  addressObject,
  nameFieldLabel = 'Deliver To',
  editable = false,
  onUpdateAddress = () => {},
  onSaveAddress = () => {},
  onDeleteAddress = () => {},
  addressType = '',
  includeBorder = false,
  compact = false,
}) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const AddressDataRef = useRef(
    GenericTableData.getTableDataBus('ADD', 'addresses'),
  );
  let AddressData = AddressDataRef.current;

  const {
    currentObject: address,
    hasBeenUpdated,
    reset: resetAddress,
    updateField,
    doSave,
  } = useUpdatableObject(AddressData.updateRecord, AddressData.createRecord);
  const addressRef = useRef();
  addressRef.current = address;

  useEffect(() => {
    DM.add(
      AddressData.registerListener('RECORD_LOADED', onAddressLoaded),
      AddressData,
    );
    DM.add(
      AddressData.registerListener('RECORD_UPDATED', onAddressUpdated),
      AddressData,
    );
    DM.add(
      AddressData.registerListener('RECORD_CREATED', onAddressCreated),
      AddressData,
    );
    DM.add(
      AddressData.registerListener('RECORDS_DELETED', onAddressDeleted),
      AddressData,
    );
  }, []);

  useEffect(() => {
    if (addressId) {
      AddressData.getRecord(addressId);
    }
  }, [addressId]);

  useEffect(() => {
    if (addressObject) {
      resetAddress(addressObject);
    }
  }, [addressObject]);

  const onAddressLoaded = (payload) => {
    if (payload) {
      resetAddress(payload);
    }
  };

  useEffect(() => {
    if (hasBeenUpdated) {
      onUpdateAddress(hasBeenUpdated);
    }
  }, [hasBeenUpdated]);

  const onAddressCreated = (payload) => {
    onAddressSaved(true, payload);
  };

  const onAddressUpdated = (payload) => {
    onAddressSaved(false, payload);
  };

  const onAddressSaved = (isNew, updatedAddress) => {
    onSaveAddress(isNew, updatedAddress);
    resetAddress(updatedAddress);
    showSaveSuccessToast(
      `${addressType ? addressType + ' ' : ''}Address ${
        isNew ? 'Created and ' : ''
      }Saved`,
    );
  };

  const doAddressDelete = () => {
    deleteAddressLinks && deleteAddressLinks(addressRef.current.id);
    onAddressDeleted();
  };
  const deleteClicked = () => {
    if (address.id) {
      openDeleteDialog({
        objectType: `${addressType ? addressType + ' ' : ''}Address`,
        onConfirmDelete: () => doAddressDelete(),
      });
    } else {
      onDeleteAddress();
    }
  };

  const onAddressDeleted = () => {
    onDeleteAddress();
    showDeletedToast(`${addressType ? addressType + ' ' : ''}Address`);
  };

  const getCurrentPrincipleSubdivisionValue = (getCity = true) => {
    let principleSubdivisionString = address?.principle_subdivision || '';
    let lastIndex = principleSubdivisionString.lastIndexOf(',');

    return getCity
      ? principleSubdivisionString.substring(0, lastIndex).trim()
      : principleSubdivisionString.substring(lastIndex + 1).trim();
  };

  const updatePrincipleSubdivision = ({ newCityValue, newStateValue }) => {
    updateField(
      'principle_subdivision',
      `${newCityValue || getCurrentPrincipleSubdivisionValue()}, ${
        newStateValue || getCurrentPrincipleSubdivisionValue(false)
      }`,
    );
  };

  let data = [
    {
      label: nameFieldLabel,
      value: address.legal_name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('legal_name', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Building Name',
      value: address.building_name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('building_name', newValue);
      },
    },
    {
      label: 'Address',
      value: address.address_or_po_box_number,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('address_or_po_box_number', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Room #',
      value: address.room_number,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('room_number', newValue);
      },
    },
    ...(!editable
      ? [
          {
            label: 'City, State Zip',
            value: `${address.principle_subdivision} ${address.postal_code}`,
          },
        ]
      : []),
    ...(editable
      ? [
          {
            label: 'City',
            value: getCurrentPrincipleSubdivisionValue(),
            editableFields: ['value'],
            onEdit: (newValue) => {
              updatePrincipleSubdivision({ newCityValue: newValue });
            },
            isRequired: true,
          },
          {
            label: 'State',
            value: getCurrentPrincipleSubdivisionValue(false),
            editableFields: ['value'],
            onEdit: (newValue) => {
              updatePrincipleSubdivision({ newStateValue: newValue });
            },
            isRequired: true,
          },
        ]
      : []),
    ...(editable
      ? [
          {
            label: 'Zip',
            value: address.postal_code,
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField('postal_code', newValue);
            },
            isRequired: true,
          },
        ]
      : []),
    {
      label: 'Country',
      value: address.country_name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('country_name', newValue);
      },
      isRequired: true,
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        width: '100%',
        alignItems: 'center',
      }}>
      <div
        className={includeBorder ? 'bp5-card' : ''}
        style={{
          width: '100%',
          ...(compact && { padding: 10 }),
        }}>
        <DetailTable data={data} editable={editable} compact={compact} />
      </div>

      {((address && !address.id) || editable) && (
        <DeleteButton
          miniVersion
          onDeleteItem={deleteClicked}
          tooltipText={'Delete Address'}
        />
      )}
      {hasBeenUpdated && <SaveButton miniVersion clickHandler={doSave} />}
    </div>
  );
};

AddressDetailTable.propTypes = {
  deleteAddressLinks: PropTypes.func,
  addressId: PropTypes.number,
  addressObject: PropTypes.object,
  nameFieldLabel: PropTypes.string,
  editable: PropTypes.bool,
  onUpdateAddress: PropTypes.func,
  onSaveAddress: PropTypes.func,
  onDeleteAddress: PropTypes.func,
  addressType: PropTypes.string,
  includeBorder: PropTypes.bool,
  compact: PropTypes.bool,
};

export default AddressDetailTable;
